import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { adminGet } from "../services/api";
import {
  ClockRegular, CheckmarkCircleRegular, DismissCircleRegular, WarningRegular, ArrowSyncRegular, NewsRegular, TranslateRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Clock = fluentIcon(ClockRegular);
const CheckCircle = fluentIcon(CheckmarkCircleRegular);
const XCircle = fluentIcon(DismissCircleRegular);
const AlertTriangle = fluentIcon(WarningRegular);
const Loader2 = fluentIcon(ArrowSyncRegular);
const Newspaper = fluentIcon(NewsRegular);
const Languages = fluentIcon(TranslateRegular);

interface IngestionLog {
  id: string;
  created_at: string;
  status: string;
  duration_ms: number;
  articles_fetched: number;
  articles_ingested: number;
  articles_skipped: number;
  articles_failed: number;
  hindi_translated: number;
  sources: { name: string; count: number }[] | null;
  errors: { title: string; error: string }[] | null;
  description: string;
}

interface IngestionStats {
  summary_24h: {
    total_runs: number;
    successful: number;
    failed: number;
    total_ingested: number;
    total_hindi: number;
    avg_duration_ms: number;
  };
  logs: IngestionLog[];
}

interface LegacyStats {
  totals: { total_articles: number; summarized: number; hi_translated: number; last_24h: number; last_hour: number };
  top_sources: { name: string; count: number }[];
}

export default function IngestionPage() {
  const { token } = useAuth();

  const { data: logs, isLoading } = useQuery({
    queryKey: ["ingestion-logs"],
    queryFn: () => adminGet<IngestionStats>("/api/v1/admin/stats/ingestion-logs", token),
    refetchInterval: 30_000,
  });

  const { data: stats } = useQuery({
    queryKey: ["ingestion-stats"],
    queryFn: () => adminGet<LegacyStats>("/api/v1/admin/stats/ingestion", token),
    refetchInterval: 60_000,
  });

  const s = logs?.summary_24h;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">News Ingestion</h1>
        <p className="text-sm text-slate-500 mt-1">Cron every 15 min. Detailed run logs with source breakdown.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card label="Runs (24h)" value={s?.total_runs ?? "—"} icon={<Clock size={16} />} />
        <Card label="Ingested (24h)" value={s?.total_ingested ?? "—"} icon={<Newspaper size={16} />} />
        <Card label="Hindi (24h)" value={s?.total_hindi ?? "—"} icon={<Languages size={16} />} />
        <Card label="Avg Duration" value={s ? `${(s.avg_duration_ms / 1000).toFixed(1)}s` : "—"} icon={<Clock size={16} />} />
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card label="Total (DB)" value={stats.totals.total_articles.toLocaleString()} />
          <Card label="AI Summarized" value={stats.totals.summarized.toLocaleString()} />
          <Card label="Hindi Done" value={stats.totals.hi_translated.toLocaleString()} />
          <Card label="Last 24h" value={stats.totals.last_24h} />
          <Card label="Last Hour" value={stats.totals.last_hour} />
        </div>
      )}

      {stats?.top_sources && stats.top_sources.length > 0 && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-slate-400 mb-3">Top Sources (24h)</h3>
          <div className="flex flex-wrap gap-2">
            {stats.top_sources.map(s => (
              <span key={s.name} className="text-xs bg-slate-800 px-3 py-1.5 rounded-full text-slate-300">
                {s.name} <span className="text-brand-blue font-semibold ml-1">{s.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-400 mb-4">Run History</h3>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Loading...</div>
        ) : (
          <div className="space-y-3">
            {(logs?.logs ?? []).map(log => (
              <div key={log.id} className={`border rounded-lg p-4 ${
                log.status === "success" ? "border-emerald-900/40 bg-emerald-950/20" :
                log.status === "error" ? "border-red-900/40 bg-red-950/20" :
                "border-yellow-900/40 bg-yellow-950/20"
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {log.status === "success" ? <CheckCircle size={14} className="text-emerald-400" /> :
                     log.status === "error" ? <XCircle size={14} className="text-red-400" /> :
                     <AlertTriangle size={14} className="text-yellow-400" />}
                    <span className="text-sm font-medium text-slate-200">
                      {new Date(log.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                    </span>
                    <span className="text-xs text-slate-500">{(log.duration_ms / 1000).toFixed(1)}s</span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-emerald-400">{log.articles_ingested} new</span>
                    <span className="text-slate-500">{log.articles_skipped} dup</span>
                    {log.articles_failed > 0 && <span className="text-red-400">{log.articles_failed} fail</span>}
                    {log.hindi_translated > 0 && <span className="text-blue-400">{log.hindi_translated} hi</span>}
                  </div>
                </div>
                {log.description && <p className="text-xs text-slate-400">{log.description}</p>}
                {log.sources && log.sources.length > 0 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {log.sources.map((s, i) => (
                      <span key={i} className="text-[10px] bg-slate-800 px-2 py-0.5 rounded text-slate-400">{s.name}: {s.count}</span>
                    ))}
                  </div>
                )}
                {log.errors && log.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {log.errors.map((e, i) => (
                      <p key={i} className="text-[10px] text-red-400 truncate">{e.title}: {e.error}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {(logs?.logs ?? []).length === 0 && <p className="text-sm text-slate-500 text-center py-4">No logs yet. Wait for next cron run (every 15 min).</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, icon }: { label: string; value: string | number; icon?: React.ReactNode }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider mb-2">{icon} {label}</div>
      <div className="text-xl font-semibold text-slate-200">{value}</div>
    </div>
  );
}
